import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlugIcon, DollarSignIcon, FileSignatureIcon, GitBranchIcon, InboxIcon, RocketIcon, ShieldAlertIcon, ImageIcon } from 'lucide-react';
import { IntegrationsPanel } from '../components/admin/IntegrationsPanel';
import { PricingPanel } from '../components/admin/PricingPanel';
import { ContractsPanel } from '../components/admin/ContractsPanel';
import { StatesPanel } from '../components/admin/StatesPanel';
import { LeadsPanel } from '../components/admin/LeadsPanel';
import { ActivationPanel } from '../components/admin/ActivationPanel';
import { MediaPanel } from '../components/admin/MediaPanel';
type Tab = 'status' | 'pricing' | 'contracts' | 'states' | 'leads' | 'media' | 'activation';
const tabs: {
  id: Tab;
  label: string;
  icon: React.ReactNode;
  title: string;
}[] = [{
  id: 'status',
  label: 'Connections',
  icon: <PlugIcon className="h-4 w-4" data-id="element-1078" data-mp="t84fwd7v1it" />,
  title: 'Integration status'
}, {
  id: 'pricing',
  label: 'Pricing rules',
  icon: <DollarSignIcon className="h-4 w-4" data-id="element-1079" data-mp="3kmqns6v8kv2j" />,
  title: 'Crew & pricing configuration'
}, {
  id: 'contracts',
  label: 'Contracts',
  icon: <FileSignatureIcon className="h-4 w-4" data-id="element-1080" data-mp="3fpi4g2ycqkzp" />,
  title: 'Contract registry'
}, {
  id: 'states',
  label: 'Booking states',
  icon: <GitBranchIcon className="h-4 w-4" data-id="element-1081" data-mp="3boy7azds3hdk" />,
  title: 'Booking states, CRM mapping & schema'
}, {
  id: 'leads',
  label: 'Leads & events',
  icon: <InboxIcon className="h-4 w-4" data-id="element-1082" data-mp="31ph0ufqpczgj" />,
  title: 'Leads, outbox & attribution'
}, {
  id: 'media',
  label: 'Photo library',
  icon: <ImageIcon className="h-4 w-4" data-id="element-1083" data-mp="2wxfjifeogijn" />,
  title: 'Owner photo library'
}, {
  id: 'activation',
  label: 'Tests & activation',
  icon: <RocketIcon className="h-4 w-4" data-id="element-1084" data-mp="2rzdzns9fgf74" />,
  title: 'Tests, owner inputs & activation'
}];
export function AdminPage() {
  const [tab, setTab] = useState<Tab>('status');
  const current = tabs.find(t => t.id === tab)!;
  return <div className="min-h-screen w-full bg-paper" data-id="element-1085" data-mp="2dzczkf0zlbsp">
      <header className="border-b border-ink-700 bg-ink" data-id="element-1086" data-mp="2e0o78pxjz3p3">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6" data-id="element-1087" data-mp="24c0rzccgqvvr">
          <div className="flex items-baseline gap-2" data-id="element-1088" data-mp="29akepeyvsbp6">
            <span className="font-display text-lg font-black text-white" data-id="element-1089" data-mp="29c56iwtt9gjc">
              <span className="text-lime" data-id="element-1090" data-mp="1uemhruh4v3bw">IMF</span> Tampa Movers
            </span>
            <span className="text-sm text-ink-300" data-id="element-1091" data-mp="1zbutbc7s0946">Staff console · Tampa local funnel</span>
          </div>
          <Link to="/" className="inline-flex min-h-[40px] items-center gap-1.5 text-sm font-semibold text-white hover:text-lime" data-id="element-1092" data-mp="2n39z5eyrh8k6">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-1093" data-mp="2s0jwrhwb0qj3" /> Customer funnel
          </Link>
        </div>
      </header>
      <div className="border-b border-ink-200 bg-white" data-id="element-1094" data-mp="2dacp7x7u4rx6">
        <p className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2 text-xs font-medium text-ink-600 sm:px-6" data-id="element-1095" data-mp="2iagu0pzdry7k">
          <ShieldAlertIcon className="h-4 w-4 shrink-0 text-magenta" aria-hidden="true" data-id="element-1096" data-mp="35zjlsqnqb7fs" />
          Preview only. In production this console sits behind authenticated, role-restricted sign-in (admin, dispatcher, crew), with every change audit-logged.
        </p>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[220px_1fr]" data-id="element-1097" data-mp="uej1cm6kpc3s">
        <nav aria-label="Console sections" className="lg:sticky lg:top-6 lg:self-start" data-id="element-1098" data-mp="192x30ts96uir">
          <ul className="flex gap-1 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0" data-id="element-1099" data-mp="143106xval94s">
            {tabs.map(t => <li key={t.id} data-id="element-1100" data-mp="gerl0esl3jmm">
                <button type="button" onClick={() => setTab(t.id)} aria-current={tab === t.id ? 'page' : undefined} className={`flex h-10 w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-[background-color,color] duration-150 ease-out ${tab === t.id ? 'bg-ink text-white' : 'text-ink-600 hover:bg-ink-100 hover:text-ink'}`} data-id="element-1101" data-mp="gft67c3bdmi2">
                  <span className={tab === t.id ? 'text-lime' : ''} data-id="element-1102" data-mp="1wuaznbxftc9j">{t.icon}</span>
                  {t.label}
                </button>
              </li>)}
          </ul>
        </nav>
        <main className="min-w-0" data-id="element-1103" data-mp="2w62qqr8kl2gv">
          <h1 className="mb-8 font-display text-3xl font-black text-ink" data-id="element-1104" data-mp="312jpefdeaz87">{current.title}</h1>
          {tab === 'status' && <IntegrationsPanel data-id="element-1105" data-mp="362frg92j8gl4" />}
          {tab === 'pricing' && <PricingPanel data-id="element-1106" data-mp="35ztaxjxnyqqr" />}
          {tab === 'contracts' && <ContractsPanel data-id="element-1107" data-mp="3b07lp9qngesh" />}
          {tab === 'states' && <StatesPanel data-id="element-1108" data-mp="3azfyonb4bpdt" />}
          {tab === 'leads' && <LeadsPanel data-id="element-1109" data-mp="3f1sscdclbwu8" />}
          {tab === 'media' && <MediaPanel data-id="element-1110" data-mp="3f0rfq4fxf2ae" />}
          {tab === 'activation' && <ActivationPanel data-id="element-1111" data-mp="3jtax7chqihkh" />}
        </main>
      </div>
    </div>;
}